import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import {
    InAppBrowser,
    MobileBrowser,
    MobileDetectionService,
} from 'app/core/services/mobile-detection.service';

/**
 * Mobile Install Gate
 *
 * Full-screen install prompt that covers the entire app on mobile
 * web browsers (non-standalone). Mounted in `AppComponent` so it
 * lives above the router-outlet and gates everything — including
 * the sign-in screen — until the user either installs the app or
 * explicitly opts into the temperamental browser experience via
 * the "Continue in browser anyway" link.
 *
 * Visibility matrix
 *  - Desktop                       → hidden (we don't push PWA there)
 *  - Mobile + standalone (installed app) → hidden (they already installed)
 *  - Mobile browser + session-dismissed  → hidden until next tab open
 *  - Mobile browser + first visit  → SHOWN
 *
 * "Continue in browser anyway" sets a session-storage flag so the
 * decision sticks for the rest of the tab's life but resets on
 * the next launch. We deliberately use sessionStorage (not
 * localStorage) so the gate re-appears the next time the user
 * opens the browser — keeps gentle pressure to install without
 * being abusive.
 *
 * Install paths
 *  - Android / Chromium mobile with a captured `beforeinstallprompt`
 *    event → big "Install App" button that fires the native
 *    prompt(). Falls back to "use the browser menu" copy when no
 *    event has fired yet (engagement heuristics, fresh visit).
 *  - iOS Safari → manual Share → Add to Home Screen instructions
 *    (iOS never fires `beforeinstallprompt`).
 */
@Component({
    selector: 'mobile-install-gate',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule],
    templateUrl: './mobile-install-gate.component.html',
    styleUrls: ['./mobile-install-gate.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileInstallGateComponent implements OnInit, OnDestroy {
    /** True when we should render the full-screen gate. Driven by
     *  `_evaluate()`; reactively updated on prompt capture, install,
     *  and dismiss. */
    visible = false;

    /** Captured `beforeinstallprompt` event — see global capture in
     *  `mobile-detection.service.ts` for the fallback path. */
    private _deferredPrompt: any = null;

    /** Latched on iOS so the template renders the manual hint. */
    isIos = false;

    /** Latched on Android-style mobile so the template renders the
     *  install button (or a "open browser menu" fallback when no
     *  prompt has been captured yet). */
    isAndroid = false;

    /** Detected browser (chrome/samsung/firefox/safari/in_app/…). */
    browser: MobileBrowser = 'other';

    /** Detected in-app browser (Instagram/Facebook/TikTok/etc) or null. */
    inAppBrowser: InAppBrowser = null;
    inAppBrowserLabel = '';

    /** Reactive — flipped to true the moment Chrome fires the
     *  install-prompt event globally (replaces the prior one-shot
     *  init-time read which left late-arriving prompts invisible). */
    private _canPromptInstall = false;

    /**
     * Bound to the iOS expand toggle. Expanded by default so the
     * Share → Add to Home Screen steps are visible immediately —
     * iOS Safari has no install button capability (Apple platform
     * restriction), so the steps ARE the install affordance.
     * Hiding them behind a collapsed toggle was reading as "no
     * download option exists" rather than "tap to expand".
     */
    iosHintExpanded = true;

    /** Session-storage key for the "Continue in browser" opt-out. */
    private static readonly DISMISS_KEY = 'mobile_install_gate_dismissed';

    /**
     * Routes where the gate should NEVER render — typically
     * pages whose entire purpose is install/onboarding and would
     * stack awkwardly under the gate. Compared against
     * `Router.url` on every NavigationEnd.
     *
     * `/resource-view` is the public link we hand to OUTSIDE
     * resources (printers, traders, etc.) to view order info via an
     * access code. They are not app users — pushing them to install
     * the PWA is wrong, so the gate must never cover this page.
     */
    private static readonly SUPPRESS_ROUTES = ['/install', '/resource-view'];

    /** Latched true when current route is in `SUPPRESS_ROUTES`. */
    private _routeSuppressed = false;

    private _unsub$ = new Subject<void>();

    constructor(
        private _mobileDetection: MobileDetectionService,
        private _cdr: ChangeDetectorRef,
        private _router: Router,
        @Inject(PLATFORM_ID) private _platformId: Object,
    ) {}

    ngOnInit(): void {
        if (!isPlatformBrowser(this._platformId)) return;

        this.isIos = this._mobileDetection.isIOS;
        this.isAndroid =
            this._mobileDetection.isAndroid ||
            (this._mobileDetection.isMobile && !this._mobileDetection.isIOS);
        this.browser = this._mobileDetection.mobileBrowser;
        this.inAppBrowser = this._mobileDetection.inAppBrowserName;
        this.inAppBrowserLabel = this._mobileDetection.inAppBrowserLabel;

        // Pick up a `beforeinstallprompt` that may have fired before
        // we mounted (e.g. captured by the global listener in
        // `mobile-detection.service.ts`).
        this._deferredPrompt = (window as any).deferredPrompt || null;

        // Subscribe to the reactive install-prompt-availability
        // BehaviorSubject so we re-render the moment Chrome fires
        // `beforeinstallprompt` — even if that's seconds after the
        // gate first mounted. The earlier pattern (one-shot read at
        // ngOnInit, plus a HostListener) silently lost late-arriving
        // events because nothing called `markForCheck` when the
        // global capture flipped the flag.
        this._mobileDetection.installPromptAvailable$
            .pipe(takeUntil(this._unsub$))
            .subscribe((available) => {
                this._canPromptInstall = available;
                if (available) {
                    this._deferredPrompt =
                        (window as any).deferredPrompt || this._deferredPrompt;
                }
                this._cdr.markForCheck();
            });

        // Track route changes so we can suppress the gate on routes
        // dedicated to install/onboarding (e.g. /install) without
        // double-rendering install UI on top of itself.
        this._routeSuppressed = this._isSuppressedUrl(this._router.url);
        this._router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntil(this._unsub$),
            )
            .subscribe((e: NavigationEnd) => {
                const next = this._isSuppressedUrl(e.urlAfterRedirects || e.url);
                if (next !== this._routeSuppressed) {
                    this._routeSuppressed = next;
                    this._evaluate();
                }
            });

        this._evaluate();

        // Best-effort installed-app detection. Async; updates the
        // template once `getInstalledRelatedApps()` resolves. Safe
        // to fire-and-forget — it can't crash the gate.
        void this._checkInstalled();
    }

    ngOnDestroy(): void {
        this._unsub$.next();
        this._unsub$.complete();
    }

    private _isSuppressedUrl(url: string): boolean {
        // Compare on the path prefix only; query strings / fragments
        // shouldn't change suppression.
        const path = (url || '').split(/[?#]/)[0];
        return MobileInstallGateComponent.SUPPRESS_ROUTES.some(
            (r) => path === r || path.startsWith(`${r}/`),
        );
    }

    /**
     * Late-arriving install prompt — usually fires after the user
     * has engaged with the page (typing into the email field on
     * the sign-in screen, etc.).
     */
    @HostListener('window:beforeinstallprompt', ['$event'])
    onBeforeInstallPrompt(event: any): void {
        event.preventDefault();
        this._deferredPrompt = event;
        (window as any).deferredPrompt = event;
        this._evaluate();
    }

    /** Hide the gate the instant the app is installed. */
    @HostListener('window:appinstalled')
    onAppInstalled(): void {
        this._deferredPrompt = null;
        (window as any).deferredPrompt = null;
        this.visible = false;
        this._cdr.markForCheck();
    }

    /**
     * Triggers the native install prompt. Must run inside the
     * user-gesture (button click) callback or Chrome rejects the
     * call silently.
     */
    async install(): Promise<void> {
        const prompt = this._deferredPrompt || (window as any).deferredPrompt;
        if (!prompt) {
            return;
        }
        try {
            prompt.prompt();
            await prompt.userChoice;
        } catch (e) {
            // Browser refused (already prompted recently, etc.). Fall
            // through; user can still use the browser-menu path.
            console.warn('PWA install prompt failed:', e);
        } finally {
            this._deferredPrompt = null;
            (window as any).deferredPrompt = null;
            // Don't auto-hide on dismiss — only on actual install
            // (handled by `appinstalled`). If the user dismissed
            // the system prompt, the gate stays so they can try
            // again or use the "Continue in browser" escape hatch.
            this._cdr.markForCheck();
        }
    }

    /** Toggle the step-by-step iOS card open / closed. */
    toggleIosHint(): void {
        this.iosHintExpanded = !this.iosHintExpanded;
        this._cdr.markForCheck();
    }

    /**
     * Escape hatch — store the opt-out in sessionStorage and hide
     * the gate. The flag resets when the tab closes / the browser
     * is relaunched, so gentle pressure to install persists across
     * sessions without being abusive within one.
     */
    continueInBrowser(): void {
        try {
            sessionStorage.setItem(
                MobileInstallGateComponent.DISMISS_KEY,
                '1',
            );
        } catch {
            // sessionStorage can throw in private-mode Safari. The
            // visibility flip below still works for this session.
        }
        this.visible = false;
        this._cdr.markForCheck();
    }

    /** True when the user can install via the native prompt right
     *  now (Android/Chromium with a captured event). Driven by the
     *  service's reactive BehaviorSubject so late-arriving events
     *  flip this on automatically. */
    get canPromptInstall(): boolean {
        return (
            this._canPromptInstall ||
            !!(this._deferredPrompt || (window as any).deferredPrompt)
        );
    }

    /** True when the user is stuck inside an in-app webview that
     *  can't install PWAs (Instagram, Facebook, TikTok, …). The
     *  template renders an "Open in Chrome / Safari" message
     *  instead of the useless fallback install instructions. */
    get isInAppBrowser(): boolean {
        return this.inAppBrowser !== null;
    }

    /** Hard reload — used by the in-app browser warning when the user
     *  copies the URL and wants to retry after switching browsers. */
    reload(): void {
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    }

    /** Copy the current URL — used by the in-app browser warning so
     *  the user can paste the page into Chrome / Safari. */
    async copyLink(): Promise<void> {
        if (typeof window === 'undefined') return;
        const url = window.location.href;
        try {
            await navigator.clipboard?.writeText(url);
        } catch {
            try {
                const ta = document.createElement('textarea');
                ta.value = url;
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            } catch {
                /* nothing else we can do */
            }
        }
        this._cdr.markForCheck();
    }

    /**
     * "Yes, I already have it" — user-driven flag. We can't auto-
     * launch the installed PWA from a website (no browser API
     * exposes that), so when the user tells us they've installed,
     * the best we can do is switch the gate into a stronger "open
     * from your home screen" state with explicit instructions.
     *
     * Latched in component state (not session storage) so a refresh
     * resets the gate to the normal install flow — prevents people
     * from getting permanently stuck if they hit the button by
     * mistake.
     */
    userClaimsInstalled = false;

    /**
     * True when `navigator.getInstalledRelatedApps()` confirms our
     * PWA is installed but they're still browsing in a tab. Same
     * UI as `userClaimsInstalled` but with a more confident copy.
     *
     * Only fires on Chromium-based browsers with the manifest
     * `related_applications` self-reference (added to
     * `manifest.webmanifest`). Safari / Firefox don't support
     * `getInstalledRelatedApps` and will simply leave this false
     * — for those users the manual "I already have it" button is
     * the only path to the strong message.
     */
    detectedInstalled = false;

    /** Template helper. */
    get showOpenFromHomeScreen(): boolean {
        return this.userClaimsInstalled || this.detectedInstalled;
    }

    /**
     * User self-reports installation. Switches the gate into the
     * "open from home screen" state and clears any previous
     * session dismiss so the message keeps reappearing across
     * navigations within this session.
     */
    iHaveItInstalled(): void {
        this.userClaimsInstalled = true;
        try {
            sessionStorage.removeItem(MobileInstallGateComponent.DISMISS_KEY);
        } catch {
            // Ignore
        }
        this._cdr.markForCheck();
    }

    /** Back-button from the strong "open from home screen" state to
     *  the default install gate (in case the user changes their
     *  mind / actually wants to install for the first time). */
    cancelClaimInstalled(): void {
        this.userClaimsInstalled = false;
        this._cdr.markForCheck();
    }

    /**
     * Best-effort browser check for whether our PWA is already
     * installed. Uses `navigator.getInstalledRelatedApps()` which
     * requires a `related_applications` entry in the manifest
     * pointing at the manifest URL itself (already in place — see
     * `src/manifest.webmanifest`).
     *
     * Returns silently on browsers that don't expose the API
     * (Safari, Firefox) — the manual "I already have it" path
     * still gets the user to the strong message.
     */
    private async _checkInstalled(): Promise<void> {
        const nav: any = navigator;
        if (typeof nav.getInstalledRelatedApps !== 'function') return;
        try {
            const apps: any[] = await nav.getInstalledRelatedApps();
            if (apps && apps.length > 0) {
                this.detectedInstalled = true;
                this._cdr.markForCheck();
            }
        } catch {
            // Permission errors / unsupported manifest entry — silent.
        }
    }

    /** Single decision point for whether the gate should be on
     *  screen. Called from `ngOnInit` and from event handlers so
     *  every state transition routes through one place. */
    private _evaluate(): void {
        if (!isPlatformBrowser(this._platformId)) {
            this.visible = false;
            return;
        }
        // Dedicated install page is its own install experience.
        // Don't stack the gate over it.
        if (this._routeSuppressed) {
            this.visible = false;
            this._cdr.markForCheck();
            return;
        }
        // Already installed — nothing to do.
        if (this._mobileDetection.isStandalone) {
            this.visible = false;
            this._cdr.markForCheck();
            return;
        }
        // Desktop — never gate.
        if (!this._mobileDetection.isMobile) {
            this.visible = false;
            this._cdr.markForCheck();
            return;
        }
        // User opted out this session — respect it.
        let dismissed = false;
        try {
            dismissed =
                sessionStorage.getItem(
                    MobileInstallGateComponent.DISMISS_KEY,
                ) === '1';
        } catch {
            dismissed = false;
        }
        if (dismissed) {
            this.visible = false;
            this._cdr.markForCheck();
            return;
        }
        // Mobile web browser, not installed, no opt-out → show gate.
        this.visible = true;
        this._cdr.markForCheck();
    }
}
